import React, { useState, useEffect } from 'react';
import { 
  ArrowLeft, Save, X, Package, Calendar, MapPin, 
  CheckCircle, XCircle, AlertCircle
} from 'lucide-react';
import { supabase } from '../../lib/supabase';

const PurchaseOrderReceive = ({ poId, onBack, onSave }) => {
  // Initialize state at the top level (before any conditional returns)
  const [formData, setFormData] = useState({
    receipt_date: new Date().toISOString().split('T')[0],
    received_by: '系統管理員', // Would come from auth system
    items: [],
    notes: ''
  });
  
  // Validation state
  const [errors, setErrors] = useState({});
  
  // State for live data
  const [po, setPo] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch data from Supabase
  useEffect(() => {
    fetchData();
  }, [poId]);
  
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch purchase order
      const { data: poData, error: poError } = await supabase
        .from('purchase_orders')
        .select('*, purchase_order_items(*)')
        .eq('id', poId)
        .single();
      
      if (poError) throw poError;
      
      // Fetch products
      const { data: productsData, error: productsError } = await supabase
        .from('products')
        .select('*');
      
      if (productsError) throw productsError;
      
      setPo(poData);
      setProducts(productsData || []);
      
      // Initialize receipt items if PO exists
      if (poData && poData.purchase_order_items) {
        const initialReceiptItems = poData.purchase_order_items.map(item => {
          const remainingToReceive = item.quantity - (item.received_quantity || 0);
          return {
            id: item.id,
            product_id: item.product_id,
            quantity: remainingToReceive > 0 ? remainingToReceive : 0,
            max_quantity: remainingToReceive,
            quality_check: 'passed',
            location: '',
            notes: ''
          };
        });
        
        setFormData(prev => ({
          ...prev,
          items: initialReceiptItems
        }));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };
  
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-gray-600">載入中...</p>
      </div>
    );
  }

  if (!po) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-xl font-medium mb-2">找不到訂單</h2>
        <p className="text-gray-500 mb-4">無法找到指定的採購訂單</p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          返回
        </button>
      </div>
    );
  }
  
  // Check if PO is in a receivable state
  const isReceivable = ['sent', 'partial_received', 'approved'].includes(po.status);
  
  if (!isReceivable) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
        <h2 className="text-xl font-medium mb-2">無法收貨</h2>
        <p className="text-gray-500 mb-4">
          此訂單狀態為「{po.status}」，無法進行收貨操作
        </p>
        <button
          onClick={onBack}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          返回
        </button>
      </div>
    );
  }
  
  // Get product details for items
  const getProductDetails = (productId) => {
    return products.find(p => p.id === productId) || {
      name: '未知產品',
      sku: ''
    };
  };
  
  // Form data is already initialized with PO items at the top level
  
  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if exists
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };
  
  // Handle item changes
  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    
    // For quantity, ensure it doesn't exceed max_quantity
    if (field === 'quantity') {
      const numValue = parseFloat(value) || 0;
      const maxQuantity = updatedItems[index].max_quantity;
      
      if (numValue > maxQuantity) {
        value = maxQuantity;
      } else if (numValue < 0) {
        value = 0;
      }
    }
    
    updatedItems[index][field] = value;
    
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
    
    // Clear error for this item if exists
    if (errors[`items[${index}].${field}`]) {
      setErrors(prev => ({
        ...prev,
        [`items[${index}].${field}`]: undefined
      }));
    }
  };
  
  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Check if at least one item has quantity > 0
    const hasItemsToReceive = formData.items.some(item => item.quantity > 0);
    if (!hasItemsToReceive) {
      newErrors.items = '至少需要一個項目有收貨數量';
    }
    
    // Validate each item
    formData.items.forEach((item, index) => {
      if (item.quantity > 0) {
        if (!item.location) {
          newErrors[`items[${index}].location`] = '請輸入儲位';
        }
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Filter out items with zero quantity
      const itemsToReceive = formData.items.filter(item => item.quantity > 0);
      
      // Create receipt object
      const receipt = {
        id: Date.now().toString(),
        po_id: po.id,
        receipt_number: `REC-${Date.now().toString().slice(-6)}`,
        receipt_date: formData.receipt_date,
        received_by: formData.received_by,
        items: itemsToReceive.map(item => ({
          id: Date.now() + Math.random().toString(36).substring(2, 9),
          po_item_id: item.id,
          product_id: item.product_id,
          quantity: item.quantity,
          quality_check: item.quality_check,
          location: item.location,
          notes: item.notes
        })),
        notes: formData.notes
      };
      
      // Determine if all items are fully received
      const updatedPoItems = po.items.map(poItem => {
        const receivedItem = itemsToReceive.find(item => item.id === poItem.id);
        const newReceivedQuantity = poItem.received_quantity + (receivedItem ? receivedItem.quantity : 0);
        return {
          ...poItem,
          received_quantity: newReceivedQuantity
        };
      });
      
      const isFullyReceived = updatedPoItems.every(item => item.received_quantity >= item.quantity);
      
      // Update PO status and history
      const updatedPo = {
        ...po,
        status: isFullyReceived ? 'received' : 'partial_received',
        items: updatedPoItems,
        history: [
          ...po.history,
          {
            id: Date.now(),
            action: isFullyReceived ? 'received' : 'partial_receipt',
            action_at: new Date().toISOString(),
            action_by: formData.received_by,
            details: `收貨: ${receipt.receipt_number}`
          }
        ]
      };
      
      // Call the save handler with both updated PO and new receipt
      if (onSave) onSave({ updatedPo, receipt });
    }
  };
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">收貨</h1>
            <p className="text-gray-500">訂單編號: {po.po_number}</p>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button 
            onClick={onBack}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center space-x-2"
          >
            <X className="h-4 w-4" />
            <span>取消</span>
          </button>
          
          <button 
            onClick={handleSubmit}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <Save className="h-4 w-4" />
            <span>確認收貨</span>
          </button>
        </div>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Receipt Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h2 className="text-lg font-medium mb-4">收貨資訊</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Receipt Date */}
                <div className="flex items-start space-x-3">
                  <div className="p-1">
                    <Calendar className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex-grow">
                    <label className="block text-sm text-gray-500 mb-1">收貨日期</label>
                    <input
                      type="date"
                      name="receipt_date"
                      value={formData.receipt_date}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg p-2"
                    />
                  </div>
                </div>
                
                {/* Received By */}
                <div className="flex items-start space-x-3">
                  <div className="p-1">
                    <Package className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex-grow">
                    <label className="block text-sm text-gray-500 mb-1">收貨人</label>
                    <input
                      type="text"
                      name="received_by"
                      value={formData.received_by}
                      onChange={handleChange}
                      className="w-full border border-gray-300 rounded-lg p-2"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
              <h2 className="text-lg font-medium mb-4">備註</h2>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg p-3 h-32"
                placeholder="輸入收貨備註..."
              ></textarea>
            </div>
          </div>
        </div>
        
        {/* Receipt Items */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-medium">收貨項目</h2>
            {errors.items && (
              <p className="text-red-500 text-sm mt-1">{errors.items}</p>
            )}
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 text-xs font-medium text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-6 py-3 text-left">產品</th>
                  <th className="px-6 py-3 text-right">訂單數量</th>
                  <th className="px-6 py-3 text-right">已收數量</th>
                  <th className="px-6 py-3 text-right">本次收貨</th>
                  <th className="px-6 py-3 text-center">品質檢查</th>
                  <th className="px-6 py-3 text-left">儲位</th>
                  <th className="px-6 py-3 text-left">備註</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white text-sm">
                {formData.items.map((item, index) => {
                  const poItem = po.items.find(i => i.id === item.id);
                  const product = getProductDetails(item.product_id);
                  
                  // Skip items that are fully received
                  if (item.max_quantity <= 0) return null;
                  
                  return (
                    <tr key={item.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <p className="text-gray-500">{product.sku}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {poItem.quantity.toLocaleString()} {poItem.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        {poItem.received_quantity.toLocaleString()} {poItem.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                          className="w-24 border border-gray-300 rounded-lg p-2 text-right"
                          min="0"
                          max={item.max_quantity}
                          step="1"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex justify-center space-x-4">
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              checked={item.quality_check === 'passed'}
                              onChange={() => handleItemChange(index, 'quality_check', 'passed')}
                              className="h-4 w-4 text-blue-600"
                            />
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer">
                            <input
                              type="radio"
                              checked={item.quality_check === 'failed'}
                              onChange={() => handleItemChange(index, 'quality_check', 'failed')}
                              className="h-4 w-4 text-blue-600"
                            />
                            <XCircle className="h-4 w-4 text-red-500" />
                          </label>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="text"
                          value={item.location}
                          onChange={(e) => handleItemChange(index, 'location', e.target.value)}
                          className={`w-full border ${errors[`items[${index}].location`] ? 'border-red-500' : 'border-gray-300'} rounded-lg p-2`}
                          placeholder="儲位"
                          disabled={item.quantity <= 0}
                        />
                        {errors[`items[${index}].location`] && (
                          <p className="text-red-500 text-xs mt-1">{errors[`items[${index}].location`]}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <input
                          type="text"
                          value={item.notes}
                          onChange={(e) => handleItemChange(index, 'notes', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg p-2"
                          placeholder="備註"
                          disabled={item.quantity <= 0}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PurchaseOrderReceive;
